import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Calendar, Loader } from '@unmind/design-system';

import { getUserLocale } from 'i18n/getUserLocale';
import { Footer, BookingError } from '../';
import { useGetDates, useGetTimeslots } from './../../hooks';
import { changeStep, formatAvailableTimes, toUtcDate } from './../../lib';

import { P, TimeSelection } from './DateAndTime.styles';
import { IDateAndTimeProps } from './types';

const Wrapper = ({
  children,
  step,
  dispatch,
  answers,
  validateAnswers,
}: {
  children: React.ReactNode;
  validateAnswers?(e: React.MouseEvent<HTMLButtonElement>): void;
} & Pick<IDateAndTimeProps, 'step' | 'dispatch' | 'answers'>) => {
  const { t } = useTranslation('book', {
    keyPrefix: 'service.form',
  });

  return (
    <>
      <fieldset>
        <h3>{t('dateAndTime.date.label')}</h3>
        {children}
      </fieldset>
      <Footer
        step={step}
        dispatch={dispatch}
        forwardCTA={{
          disabled: !answers.date || !answers.times || !answers.times.length,
          validate: validateAnswers ? validateAnswers : () => null,
        }}
      />
    </>
  );
};

export default ({
  answers,
  dispatch,
  errors,
  formData,
  service,
  step,
}: IDateAndTimeProps) => {
  const { t } = useTranslation('book', {
    keyPrefix: 'service.form',
  });
  const locale = getUserLocale();

  const {
    data: dateData,
    loading: isLoadingDates,
    error: isDateError,
  } = useGetDates(service.id, formData['0'].email, formData['1']);
  const availableDates = dateData?.availableBookingDates.availableDates || [];

  const {
    data: timeData,
    loading: isLoadingTimes,
    error: isTimeError,
  } = useGetTimeslots(
    service.id,
    formData['0'].email,
    formData['1'],
    answers.date,
  );
  const availableTimes = formatAvailableTimes(
    locale,
    timeData?.availableBookingTimes?.timeSlots,
  );

  useEffect(() => {
    if (
      !answers.date &&
      dateData?.availableBookingDates.availableDates?.length
    ) {
      dispatch([
        {
          type: 'answers.2.date',
          value: new Date(
            `${dateData?.availableBookingDates.availableDates[0]}T00:00:00Z`,
          ),
        },
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateData?.availableBookingDates.availableDates]);

  const validateAnswers = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (!answers.date || isNaN(answers.date.getTime())) {
      dispatch({
        type: 'errors.date',
        value: t('dateAndTime.date.error.required'),
      });

      return;
    }

    if (!answers.times || !answers.times.length) {
      dispatch({
        type: 'errors.times',
        value: t('dateAndTime.time.error.required'),
      });

      return;
    }

    changeStep({ step, dispatch });
  };

  const wrapperProps = {
    step,
    validateAnswers,
    dispatch,
    answers,
  };

  if (isLoadingDates) {
    return (
      <Wrapper {...wrapperProps}>
        <Loader variant="text" text={t('dateAndTime.date.loader')} />
      </Wrapper>
    );
  }

  if (isDateError || isTimeError) {
    return (
      <Wrapper {...wrapperProps}>
        <BookingError />
      </Wrapper>
    );
  }

  return (
    <Wrapper {...wrapperProps}>
      {availableDates.length ? (
        <Calendar
          className="sass-migrated"
          selectedDate={answers.date || availableDates?.[0] || undefined}
          availableDates={availableDates}
          functions={{
            onDateSelect: date => {
              dispatch([
                { type: 'answers.2.date', value: toUtcDate(date) },
                { type: 'answers.2.times', value: [] },
                { type: 'errors.date', value: undefined },
              ]);
            },
          }}
        />
      ) : (
        <P className="no-slots" data-testid="no-slots-dates">
          {t('dateAndTime.date.error.noSlots')}
        </P>
      )}

      {errors?.date ? (
        <Alert
          className="card error"
          title={t('dateAndTime.date.error.required')}
          variant="error"
          size="small"
        />
      ) : (
        <></>
      )}

      {isLoadingTimes ? (
        <Loader variant="text" text={t('dateAndTime.time.loader')} />
      ) : (
        <></>
      )}

      {!isLoadingTimes && availableDates.length ? (
        availableTimes?.length ? (
          <section data-testid="time-selection">
            <h3>
              {t('dateAndTime.time.label', {
                date: answers.date?.toLocaleDateString(locale, {
                  dateStyle: 'full',
                  timeZone: 'UTC',
                }),
              })}
            </h3>
            <TimeSelection
              type="checkbox"
              className="timeselection"
              legend={t('dateAndTime.time.subtitle')}
              options={availableTimes}
              onSelected={values =>
                dispatch([
                  {
                    type: 'answers.2.times',
                    value: values,
                  },
                  { type: 'errors.times', value: undefined },
                ])
              }
              values={answers.times || []}
            />
          </section>
        ) : (
          <P className="no-slots" data-testid="no-slots-times">
            {t('dateAndTime.time.error.noSlots')}
          </P>
        )
      ) : (
        <></>
      )}

      {errors?.times ? (
        <Alert
          className="card error"
          title={t('dateAndTime.time.error.required')}
          variant="error"
          size="small"
        />
      ) : (
        <></>
      )}
    </Wrapper>
  );
};
